/**
 * The two A1 lifecycle surfaces on the work-order record: TESTING ROUNDS and
 * PAYMENT STATUS. (A2's deployment and sign-off panels land in this file too.)
 *
 * ===========================================================================
 * A FAILED ROUND ROUTES THE WORK, AND THE SCREEN REPORTS WHERE IT WENT
 * ===========================================================================
 * `POST /work-orders/:id/test-rounds` does not just record a verdict. On `fail`
 * the service moves the work order — back to the developer, or to the country's
 * configured returnee per `countries.tester_return` — and fires the notification
 * that puts it on somebody's bell. That routing decision is ON THE WIRE as
 * `work_order_status` in the response, which is why this panel reads it from the
 * response rather than predicting it: the per-country rule lives in the database,
 * and a client that guessed "fail → developer" would be right in India and wrong
 * somewhere else the first time the setting changed.
 *
 * ===========================================================================
 * PAYMENT STATUS IS ITS OWN SURFACE, AND THAT IS THE POINT (owner-ruled)
 * ===========================================================================
 * It was deliberately kept OFF the general CRF edit so that a commercial fact
 * cannot change as a side effect of renaming a title. `POST /crfs/:id/payment-status`
 * is its own route, gated on its own key (`billing.record`), and it emits its own
 * routed event. This panel is the ONE CONTROL in the app that moves it, and it is
 * rendered as a separate card for exactly that reason — folding it into a form
 * with the title and the description would undo the ruling in the UI while
 * honouring it in the API.
 *
 * ⚠ AMENDED (Train E, fork 15): this paragraph used to call the panel the sole
 * *place* that moves the status — true when the work-order record was the one
 * screen mounting it. The CRF record (`features/crfs/CrfRecordPage.tsx`) now
 * mounts the SAME panel as its own separate card, so the fact is recordable where
 * the CRF is read without opening a work order (an advance learned after
 * confirmation, before any work is cut — *"frozen, the workaround is a
 * spreadsheet"*). What the owner forbade is payment status folded into a CRF
 * EDIT FORM; what he endorsed in the same sentence is a SEPARATE CARD, and that
 * is what both screens mount. One control, two doors, same route, same key.
 * `crfs/record-comment-honesty.test.ts` reddens if the old "only" wording returns
 * here or in `domain/nav.ts` while both mounts stand.
 *
 * ===========================================================================
 * THE PER-POINT GRID IS AN ENTRY SURFACE, NOT A REPORT (owner ruling 2)
 * ===========================================================================
 * "Deploy REQUIRES every test point passed" is a rule about POINTS, and until this
 * panel carried them there was nowhere in any browser to record one. `test_rounds`
 * holds ONE result for a whole round, so a tester could say "round 3 passed" and
 * nothing anywhere said which of the eleven requirements that covered.
 *
 * The grid rendered here is `coverage.points` — the same array the API computes the
 * coverage answer from, arriving on the same read. That identity is the point: the
 * write ECHOES each description back and the server refuses a byte that drifted
 * (`POINT_TEXT_STALE`), so a grid fetched from one request and a coverage computed
 * in another can disagree, and the tester meets that disagreement as a refusal for a
 * mismatch this screen never showed them.
 *
 * NOTHING HERE BLOCKS ANYTHING. The verdicts are recorded; the refusal that reads
 * them is a later commit. A client that refused what the server accepts would be a
 * second implementation of a rule that does not exist yet.
 *
 * NOTE ON MONEY: neither panel renders an amount. The figure lives on the CRF card
 * where the masking interceptor's output is displayed verbatim; payment STATUS
 * ("received", "pending") is not money and is not masked — a Developer may see
 * that a CRF is paid without seeing what it is worth.
 */

import { useState } from 'react';

import { Alert, Button } from '../../components/ui';
import type {
  CoveragePointDto,
  PaymentStatus,
  TestPointVerdict,
  TestResult,
  TestRoundPointInput,
  WorkOrderRecordDto,
} from './api';
import { GAP_LABEL, sortedGrid } from './coverage';
import { useRecordRound, useSetPaymentStatus } from './use-work';
import { workErrorText } from './errors';
// The label map and the offer order live beside the wire type, not in this component
// file — the CRF record's Commercials row reads the same map (fork 15, one vocabulary).
import { PAYMENT_LABEL, PAYMENT_ORDER } from './payment-status';
import type { ToastTone } from '../inquiries/toast';
import type { CountryCode } from '../../domain/countries';

const RESULT_LABEL: Readonly<Record<TestResult, string>> = {
  in_test: 'In test',
  pass: 'Passed',
  fail: 'Failed',
};

/**
 * The per-point verdicts, with the words a tester uses. `Record<TestPointVerdict,…>`
 * for the same reason as {@link PAYMENT_LABEL}: adding a verdict to the API's union
 * breaks this build until somebody writes its label.
 *
 * `not_applicable` reads "Not applicable" and NOT "Skip". It is owner fork 18 — the
 * only lever for a requirement the customer withdrew, because a point cannot be
 * cancelled and Cancel CRF was never built — and its remark is mandatory. A control
 * labelled "Skip" invites exactly the use the mandatory remark exists to prevent.
 */
const VERDICT_LABEL: Readonly<Record<TestPointVerdict, string>> = {
  pass: 'Pass',
  fail: 'Fail',
  not_applicable: 'Not applicable',
};

/** Pass first: it is the common case, and the destructive one should not be nearest. */
const VERDICT_ORDER: readonly TestPointVerdict[] = ['pass', 'fail', 'not_applicable'];

/* ──────────────────────────────────────────────────────────── testing rounds ── */

export function TestingPanel({
  record,
  countryCode,
  canRecord,
  onToast,
}: {
  record: WorkOrderRecordDto;
  countryCode: CountryCode;
  canRecord: boolean;
  onToast: (text: string, tone: ToastTone) => void;
}): React.JSX.Element {
  const mutation = useRecordRound(countryCode);
  const [note, setNote] = useState('');
  /** What the SERVER said the last round did to this work order. Never predicted. */
  const [routed, setRouted] = useState<string | null>(null);
  /**
   * The verdicts clicked in THIS sitting, by point number. `Map`, not an object:
   * `noUncheckedIndexedAccess` makes `get()` return `undefined` explicitly, which is
   * the difference between "not judged" and "judged" that the body-builder branches
   * on. Cleared on success — a stale click resubmitted against the NEXT round would
   * record a verdict nobody gave.
   */
  const [verdicts, setVerdicts] = useState<ReadonlyMap<number, TestPointVerdict>>(new Map());
  const [remarks, setRemarks] = useState<ReadonlyMap<number, string>>(new Map());

  const grid = sortedGrid(record.coverage);

  /**
   * THE REQUEST BODY, and every field of it is load-bearing.
   *
   * Built from `grid` — the SORTED array that was rendered — so each `point_no`
   * travels beside the `description` that was on screen next to it. `description` is
   * the ECHO the server compares with `===`; it is passed through untouched, never
   * trimmed or re-cased, because the refusal it feeds (`POINT_TEXT_STALE`) is the
   * one that stops a verdict being recorded against words the tester never read.
   *
   * A point with no verdict is OMITTED rather than sent as null: the round records
   * what was judged, and fork 9 says a point's most recent verdict counts wherever
   * it was recorded, so an unjudged point simply keeps whatever stood before.
   */
  const judged: TestRoundPointInput[] = grid.flatMap((point) => {
    const verdict = verdicts.get(point.point_no);
    if (verdict === undefined) return [];
    const remark = (remarks.get(point.point_no) ?? '').trim();
    return [
      {
        point_no: point.point_no,
        description: point.description,
        verdict,
        remark: remark === '' ? undefined : remark,
      },
    ];
  });

  const submit = (result: TestResult): void => {
    setRouted(null);
    mutation.mutate(
      {
        workOrderId: record.id,
        result,
        note: note.trim() === '' ? null : note.trim(),
        // UNDEFINED, never `[]`, when nothing was judged. An un-upgraded server
        // refuses an unknown property outright (measured: 400, zero rows), so a
        // round with no per-point evidence must go on the wire exactly as it always
        // did. `apiRequest` JSON-stringifies, which drops an undefined key.
        points: judged.length === 0 ? undefined : judged,
      },
      {
        onSuccess: (round) => {
          setNote('');
          setVerdicts(new Map());
          setRemarks(new Map());
          setRouted(round.work_order_status);
          onToast(
            result === 'fail'
              ? `Round ${String(round.round_no)} failed. The work order is now ${round.work_order_status.replace(/_/g, ' ')}.`
              : `Round ${String(round.round_no)} recorded as ${RESULT_LABEL[result].toLowerCase()}.`,
            'success',
          );
        },
        onError: (error) => onToast(workErrorText(error), 'error'),
      },
    );
  };

  return (
    <section
      aria-labelledby="testing-heading"
      className="rounded-md border border-line bg-surface"
    >
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <h2 id="testing-heading" className="text-body font-medium text-fg">
          Testing
        </h2>
        <p className="text-label text-fg-muted">
          {record.rounds.length === 0
            ? 'No rounds yet'
            : `${String(record.rounds.length)} ${record.rounds.length === 1 ? 'round' : 'rounds'}`}
        </p>
      </header>

      <div className="flex flex-col gap-3 p-4">
        {record.rounds.length === 0 ? (
          <p className="text-body13 text-fg-muted">
            Nothing has been tested yet. Round numbers are allocated by the server when a
            round is recorded, so they cannot be typed or reused.
          </p>
        ) : (
          <ol className="flex flex-col gap-2">
            {record.rounds.map((round) => (
              <li
                key={round.id}
                // Addressed by ROUND NUMBER, not by row index: the smoke asserts
                // "round 2 passed", and an index would silently retarget if the
                // list order or a filter ever changed.
                data-testid={`round-${String(round.round_no)}`}
                className="flex flex-col gap-0.5 rounded-sm border border-line px-3 py-2"
              >
                <p className="flex items-center gap-2 text-body13 text-fg">
                  <span className="tnum font-medium">Round {String(round.round_no)}</span>
                  <span
                    data-result={round.result}
                    className={
                      round.result === 'fail'
                        ? 'rounded-pill bg-danger-bg px-2 py-0.5 text-label text-danger-fg'
                        : round.result === 'pass'
                          ? 'rounded-pill bg-ok-bg px-2 py-0.5 text-label text-ok-fg'
                          : 'rounded-pill bg-subtle px-2 py-0.5 text-label text-fg-muted'
                    }
                  >
                    {RESULT_LABEL[round.result]}
                  </span>
                  <span className="tnum ml-auto text-label text-fg-muted">
                    {new Date(round.created_at).toLocaleDateString()}
                  </span>
                </p>
                {round.note === null ? null : (
                  <p className="text-body13 text-fg-secondary">{round.note}</p>
                )}
              </li>
            ))}
          </ol>
        )}

        {/* ────────────────────────────── the per-point grid (owner ruling 2) ── */}
        <div className="flex flex-col gap-2 border-t border-line pt-3">
          <p className="text-label font-medium text-fg-muted">Test points</p>
          {grid.length === 0 ? (
            /*
             * OWNER FORK 3, said plainly. A round recorded against a CRF with no
             * points proves nothing about any requirement, because there are none —
             * and this is not a corner case: measured, every CRF at or past testing
             * on this database carries zero points. The sentence names the fix
             * (add them to the CRF's grid) rather than only the problem.
             *
             * No link: `domain/nav.ts` reserves `crfs` with `available: false` and
             * no route is mounted, so a link styled as one would 404.
             */
            <p className="text-body13 text-fg-muted" data-testid="testing-no-points">
              This CRF carries no test points, so a round here records that testing
              happened without recording what it covered. Requirements are added on the
              CRF&rsquo;s own grid.
            </p>
          ) : (
            <ol className="flex flex-col gap-2">
              {grid.map((point) => (
                <TestPointRow
                  key={point.point_no}
                  point={point}
                  canRecord={canRecord}
                  chosen={verdicts.get(point.point_no)}
                  remark={remarks.get(point.point_no) ?? ''}
                  onVerdict={(verdict) =>
                    setVerdicts((previous) => {
                      const next = new Map(previous);
                      // Clicking the standing choice again CLEARS it, so a misclick
                      // has an undo that is not "reload the page and lose the note".
                      if (next.get(point.point_no) === verdict) next.delete(point.point_no);
                      else next.set(point.point_no, verdict);
                      return next;
                    })
                  }
                  onRemark={(text) =>
                    setRemarks((previous) => new Map(previous).set(point.point_no, text))
                  }
                />
              ))}
            </ol>
          )}
        </div>

        {/* D5: no `testing.record` → no form. The history above stays readable. */}
        {canRecord ? (
          <div className="flex flex-col gap-2 border-t border-line pt-3">
            <label htmlFor="round-note" className="text-label font-medium text-fg-muted">
              What happened? (optional, but a failure without one helps nobody)
            </label>
            <textarea
              id="round-note"
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="e.g. Terms block still prints on page 2 for landscape stationery."
              className="w-full rounded-sm border border-line bg-surface px-2 py-1.5 text-body13 text-fg placeholder:text-fg-muted"
            />
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                disabled={mutation.isPending}
                onClick={() => submit('in_test')}
              >
                Open a round
              </Button>
              <Button
                variant="ghost"
                disabled={mutation.isPending}
                onClick={() => submit('fail')}
              >
                {mutation.isPending ? 'Recording…' : 'Record a failure'}
              </Button>
              <Button disabled={mutation.isPending} onClick={() => submit('pass')}>
                Record a pass
              </Button>
            </div>
            {judged.length === 0 ? null : (
              // What rides along, stated before the click. The round's own result and
              // the per-point verdicts are DIFFERENT facts — a round can be recorded
              // as failed while nine of its ten points passed — so a tester who has
              // been clicking verdicts needs to see that they are about to be sent.
              <p className="text-label text-fg-muted" data-testid="verdicts-pending">
                {judged.length === 1
                  ? '1 point verdict will be recorded with this round.'
                  : `${String(judged.length)} point verdicts will be recorded with this round.`}
              </p>
            )}
            <p className="text-label text-fg-muted">
              A failure sends the work back and tells whoever it goes to. Where it goes is a
              per-country setting, so this screen reports the server&rsquo;s answer rather
              than assuming the developer.
            </p>
            {routed === null ? null : (
              <Alert tone="info" title="Where the work went">
                The work order is now <strong>{routed.replace(/_/g, ' ')}</strong> — that is
                the server&rsquo;s routing decision, not this screen&rsquo;s guess.
              </Alert>
            )}
            {mutation.isError ? (
              <Alert tone="danger" title="The round was not recorded">
                {workErrorText(mutation.error)}
              </Alert>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * ONE requirement: what it says, where it stands, and the three verdicts.
 *
 * ===========================================================================
 * THE DESCRIPTION IS RENDERED VERBATIM, AND THAT IS A CORRECTNESS RULE
 * ===========================================================================
 * The text on this row is the byte string the request echoes back, and the server
 * compares it with `===` — deliberately in TypeScript, because `crf_points` is
 * `utf8mb4_0900_ai_ci` and, measured on this stack, `'Column'` and `'column'` are
 * ONE string to that database. So nothing here may truncate it with a CSS ellipsis
 * that also trims the value, title-case it, or collapse its whitespace: the rendered
 * text and the submitted text must be the same text, or a tester judges one wording
 * and records a verdict against another.
 *
 * ===========================================================================
 * THE REMARK IS OFFERED, NOT POLICED
 * ===========================================================================
 * A remark is MANDATORY for `not_applicable` (owner fork 18) — and that rule is
 * enforced in the service, which can name the offending point numbers, and again by
 * `chk_test_round_points_na_remark` for writers that never see the DTO. This screen
 * supplies the field and says it is required; it does NOT block the submit. A second
 * copy of the rule here would be a second thing to keep in step, and the server's
 * refusal already arrives naming the exact points, which `workErrorText` renders.
 */
function TestPointRow({
  point,
  canRecord,
  chosen,
  remark,
  onVerdict,
  onRemark,
}: {
  point: CoveragePointDto;
  canRecord: boolean;
  chosen: TestPointVerdict | undefined;
  remark: string;
  onVerdict: (verdict: TestPointVerdict) => void;
  onRemark: (text: string) => void;
}): React.JSX.Element {
  const remarkId = `point-remark-${String(point.point_no)}`;
  return (
    <li
      // Addressed by POINT NUMBER, never by row index — the same reason the rounds
      // list above is: `crf_points` is DELETEd and re-INSERTed 1..N on every grid
      // save, so an index is a slot and the number is the requirement.
      data-testid={`point-${String(point.point_no)}`}
      className="flex flex-col gap-1.5 rounded-sm border border-line px-3 py-2"
    >
      <p className="flex items-baseline gap-2 text-body13 text-fg">
        <span className="tnum shrink-0 font-medium">{point.point_no}.</span>
        <span className="min-w-0">{point.description}</span>
      </p>

      <p className="flex flex-wrap items-center gap-2">
        {/* WHERE IT STANDS — the server's answer, not this screen's. `verdict` is
          * the most recent one that still describes THIS number and THESE words
          * (owner fork 9), and `gap` is null exactly when the point is accounted
          * for, so the two are rendered from the payload rather than re-derived. */}
        {point.verdict === null ? (
          <span className="text-label text-fg-muted">No verdict recorded</span>
        ) : (
          <span
            data-verdict={point.verdict}
            className={
              point.verdict === 'fail'
                ? 'rounded-pill bg-danger-bg px-2 py-0.5 text-label text-danger-fg'
                : point.verdict === 'pass'
                  ? 'rounded-pill bg-ok-bg px-2 py-0.5 text-label text-ok-fg'
                  : 'rounded-pill bg-subtle px-2 py-0.5 text-label text-fg-muted'
            }
          >
            {VERDICT_LABEL[point.verdict]}
            {point.round_no === null ? '' : ` · round ${String(point.round_no)}`}
          </span>
        )}
        {point.gap === null ? null : (
          <span data-gap={point.gap} className="text-label text-warn-fg">
            {GAP_LABEL[point.gap]}
          </span>
        )}
        {point.remark === null ? null : (
          <span className="text-label text-fg-secondary">{point.remark}</span>
        )}
      </p>

      {/* D5 again: without `testing.record` the controls are ABSENT and the state
        * above stays readable. A developer may see which requirements failed; they
        * may not judge them. */}
      {canRecord ? (
        <div
          role="group"
          // The group carries the identity so the three buttons can stay one short
          // word each: a screen reader announces "Point 2, …" before "Pass", and
          // three buttons labelled "Pass" with no context is the alternative.
          aria-label={`Point ${String(point.point_no)}: ${point.description}`}
          className="flex flex-wrap items-center gap-2"
        >
          {VERDICT_ORDER.map((verdict) => (
            <button
              key={verdict}
              type="button"
              aria-pressed={chosen === verdict}
              onClick={() => onVerdict(verdict)}
              className={`rounded-sm border px-2.5 py-1 text-body13 font-medium ${
                chosen === verdict
                  ? 'border-indigo bg-subtle text-fg'
                  : 'border-line bg-surface text-fg hover:border-line-strong'
              }`}
            >
              {VERDICT_LABEL[verdict]}
            </button>
          ))}
        </div>
      ) : null}

      {canRecord && chosen !== undefined ? (
        <>
          <label htmlFor={remarkId} className="text-label text-fg-muted">
            {chosen === 'not_applicable'
              ? 'Why this requirement no longer applies (required)'
              : 'Remark (optional)'}
          </label>
          <input
            id={remarkId}
            type="text"
            value={remark}
            maxLength={300}
            onChange={(event) => onRemark(event.target.value)}
            className="w-full rounded-sm border border-line bg-surface px-2 py-1 text-body13 text-fg placeholder:text-fg-muted"
          />
        </>
      ) : null}
    </li>
  );
}

/* ─────────────────────────────────────────────────────────── payment status ── */

export function PaymentPanel({
  crfId,
  current,
  countryCode,
  canRecord,
  onToast,
}: {
  crfId: string;
  current: PaymentStatus | null;
  countryCode: CountryCode;
  canRecord: boolean;
  onToast: (text: string, tone: ToastTone) => void;
}): React.JSX.Element {
  const mutation = useSetPaymentStatus(countryCode);

  return (
    <section
      aria-labelledby="payment-heading"
      className="rounded-md border border-line bg-surface"
    >
      <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
        <h2 id="payment-heading" className="text-body font-medium text-fg">
          Payment status
        </h2>
        <p className="text-label text-fg-muted" data-testid="payment-current">
          {current === null ? 'Not set' : PAYMENT_LABEL[current]}
        </p>
      </header>

      <div className="flex flex-col gap-3 p-4">
        {/* "Through this card" rather than "here": the same card sits on the work-order
          * record and on the CRF record, and a reader who meets it twice must not be told
          * each time that it is the one place. It IS the one control. */}
        <p className="text-body13 text-fg-secondary">
          Payment is recorded through this card and nowhere else — it is deliberately not part
          of editing the CRF, so it cannot change as a side effect of another edit.
        </p>

        {canRecord ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {PAYMENT_ORDER.map((status) => (
                <button
                  key={status}
                  type="button"
                  aria-pressed={current === status}
                  disabled={mutation.isPending || current === status}
                  onClick={() =>
                    mutation.mutate(
                      { crfId, paymentStatus: status },
                      {
                        onSuccess: () =>
                          onToast(`Payment status is now ${PAYMENT_LABEL[status]}.`, 'success'),
                        onError: (error) => onToast(workErrorText(error), 'error'),
                      },
                    )
                  }
                  className={`rounded-sm border px-2.5 py-1 text-body13 font-medium disabled:opacity-60 ${
                    current === status
                      ? 'border-indigo bg-subtle text-fg'
                      : 'border-line bg-surface text-fg hover:border-line-strong'
                  }`}
                >
                  {PAYMENT_LABEL[status]}
                </button>
              ))}
            </div>
            {mutation.isError ? (
              <Alert tone="danger" title="The payment status was not changed">
                {workErrorText(mutation.error)}
              </Alert>
            ) : null}
          </>
        ) : (
          // D5 again: absent, not disabled. The current state is still readable —
          // a Developer may know a job is paid; they may not set it.
          <p className="text-body13 text-fg-muted">
            Recording payment is not part of your access.
          </p>
        )}
      </div>
    </section>
  );
}
